import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { AbiInvio } from "@/pages/abi/AbiInvio";
import { Landmark, FileText, UserPlus, UserCheck, ExternalLink, Loader2, Shield, HeartHandshake, CloudLightning, Home, Activity, Send, Euro, FileCheck } from "lucide-react";

const SUITE = [
  { t: "rc", label: "RC Economia Solidale", icon: Shield },
  { t: "mrv", label: "Multirischi Volontariato", icon: HeartHandshake },
  { t: "cauzioni", label: "Cauzioni", icon: Landmark },
  { t: "mup", label: "MUP", icon: FileText },
];
const PORTALE = [
  { t: "catnat", label: "CAT NAT", icon: CloudLightning },
  { t: "danni_beni", label: "Danni ai Beni", icon: Home },
  { t: "infortuni", label: "Infortuni Economia Solidale", icon: Activity },
];

const STATO_INVIO = {
  inviato: { label: "Inviato", cls: "bg-blue-100 text-blue-700" },
  in_attesa_conferma: { label: "In attesa conferma OTP", cls: "bg-amber-100 text-amber-700" },
  ricezione_confermata: { label: "Ricezione confermata", cls: "bg-emerald-100 text-emerald-700" },
  errore_invio: { label: "Invio fallito", cls: "bg-red-100 text-red-700" },
};

export function AbiAzioni({ id }) {
  const nav = useNavigate();
  const [ana, setAna] = useState(null);
  const [busy, setBusy] = useState("");
  const [invio, setInvio] = useState(false);
  const [invii, setInvii] = useState([]);
  const [premi, setPremi] = useState([]);
  const loadInvii = () => api.get(`/abi/analyses/${id}/invii`).then(({ data }) => setInvii(data)).catch(() => {});
  useEffect(() => {
    loadInvii();
    api.get(`/abi/analyses/${id}/premi`).then(({ data }) => setPremi(data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);
  const premioDi = (t) => premi.find((p) => p.target === t);
  const apriAttestato = async (iid) => {
    const win = window.open("about:blank", "_blank");
    try {
      const { data } = await api.get(`/abi/invii/${iid}/attestato/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      if (win) win.location.href = url; else window.open(url, "_blank");
    } catch { if (win) win.close(); toast.error("Attestato non disponibile"); }
  };

  const loadAna = () => api.get(`/abi/analyses/${id}/anagrafica`).then(({ data }) => setAna(data)).catch(() => {});
  useEffect(() => { loadAna(); /* eslint-disable-next-line */ }, [id]);

  const aggiungi = async () => {
    setBusy("ana");
    try {
      const { data } = await api.post(`/abi/analyses/${id}/anagrafica`);
      toast.success(data.creata ? `${data.ragione_sociale} aggiunta alle anagrafiche` : "Anagrafica già presente");
      loadAna();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const precompila = async (target, label) => {
    const portale = PORTALE.some((p) => p.t === target);
    const win = portale ? window.open("about:blank", "_blank") : null;
    setBusy(target);
    try {
      const { data } = await api.post(`/abi/analyses/${id}/precompila`, { target });
      if (data.external) {
        if (data.dati_testo) navigator.clipboard?.writeText(data.dati_testo).catch(() => {});
        if (win) win.location.href = data.url; else window.open(data.url, "_blank");
        toast.success(`Portale Partner aperto su ${data.prodotto}. Dati cliente copiati: incollali se il Portale non li precompila.`, { duration: 7000 });
        return;
      }
      if (target === "cauzioni" || target === "mup") toast.success(`Bozza ${label} creata`);
      else toast.success(`Preventivatore ${label} aperto con l'ente selezionato. Nessun preventivo salvato finché non confermi.`);
      loadAna();
      nav(data.url);
    } catch (e) {
      if (win) win.close();
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(""); }
  };

  const Btn = ({ item, ext }) => {
    const Icon = item.icon;
    return (
      <Button variant="outline" size="sm" disabled={!!busy} onClick={() => precompila(item.t, item.label)} data-testid={`abi-precompila-${item.t}`}>
        {busy === item.t ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Icon className="h-4 w-4 mr-1.5" />}
        {item.label}{ext && <ExternalLink className="h-3 w-3 ml-1.5 text-slate-400" />}
      </Button>
    );
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 mb-6" data-testid="abi-azioni">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <h3 className="font-heading font-semibold text-slate-800 text-sm">Azioni sul cliente</h3>
        <div className="flex items-center gap-2 flex-wrap">
        <Button size="sm" onClick={() => setInvio(true)} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="abi-invia-cliente"><Send className="h-4 w-4 mr-1.5" /> Invia al cliente</Button>
        {ana?.esiste ? (
          <button onClick={() => nav("/anagrafiche")} className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 transition-colors" data-testid="abi-anagrafica-presente">
            <UserCheck className="h-3.5 w-3.5" /> Già in anagrafica
          </button>
        ) : (
          <Button size="sm" onClick={aggiungi} disabled={!!busy || !ana} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="abi-aggiungi-anagrafica">
            {busy === "ana" ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <UserPlus className="h-4 w-4 mr-1.5" />} Aggiungi ad anagrafiche
          </Button>
        )}
        </div>
      </div>
      <p className="text-[11px] uppercase tracking-wide text-slate-400 mb-2">Pre-compila nella Suite</p>
      <div className="flex flex-wrap gap-2 mb-4">{SUITE.map((i) => <Btn key={i.t} item={i} />)}</div>
      <p className="text-[11px] uppercase tracking-wide text-slate-400 mb-2">Pre-compila sul Portale Partner</p>
      <div className="flex flex-wrap gap-2">{PORTALE.map((i) => (
        <div key={i.t} className="flex flex-col items-start gap-1">
          <Btn item={i} ext />
          <span className="text-[10px] text-slate-400 inline-flex items-center gap-0.5 pl-1" data-testid={`abi-premio-${i.t}`}>
            <Euro className="h-3 w-3" />{premioDi(i.t)?.disponibile ? `Premio stimato € ${Number(premioDi(i.t).lordo_annuo).toLocaleString("it-IT", { minimumFractionDigits: 2 })}` : "Premio non ancora disponibile"}
          </span>
        </div>
      ))}</div>
      <p className="text-[11px] text-slate-400 mt-3">RC e Multirischi aprono il preventivatore con l'ente già selezionato (se non c'è, viene aggiunto alle anagrafiche). Cauzioni e MUP creano una bozza da completare. CAT NAT, Danni ai Beni e Infortuni aprono il Portale Partner in una nuova scheda.</p>
      {invii.length > 0 && (
        <div className="mt-4 pt-3 border-t border-slate-100" data-testid="abi-invii">
          <p className="text-[11px] uppercase tracking-wide text-slate-400 mb-1.5">Invii al cliente</p>
          <div className="space-y-1.5">
            {invii.map((v) => (
              <div key={v.id} className="flex items-center justify-between gap-3 text-xs flex-wrap" data-testid={`abi-invio-${v.id}`}>
                <span className="text-slate-600">{new Date(v.inviato_at).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" })} · {v.to.join(", ")} · {v.documenti.length} documenti{v.questionario_url ? " + questionario" : ""}</span>
                <span className={`px-1.5 py-0.5 rounded ${STATO_INVIO[v.stato]?.cls || "bg-slate-100 text-slate-600"}`}>
                  {STATO_INVIO[v.stato]?.label || v.stato}{v.conferma?.at ? ` il ${new Date(v.conferma.at).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" })}` : ""}
                </span>
                {v.conferma && (
                  <button onClick={() => apriAttestato(v.id)} className="inline-flex items-center gap-1 text-teal-700 hover:underline" data-testid={`abi-attestato-${v.id}`}>
                    <FileCheck className="h-3.5 w-3.5" /> Attestato di ricezione
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      <AbiInvio id={id} open={invio} onOpenChange={setInvio} onSent={loadInvii} />
    </div>
  );
}
